import React, { useState } from 'react';
import { Check, X, Clock, Save } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { generateId } from '../../utils/helpers';
import DateInput from '../../components/common/DateInput';

function StudentAttendance() {
  const { attendance, setAttendance, students, classes } = useApp();
  const { currentUser } = useAuth();
  const isTeacher = currentUser?.role === 'teacher';
  const availableClasses = isTeacher
    ? classes.filter(c => c.classTeacherId === currentUser?.profileId)
    : classes;
  const today = new Date().toISOString().split('T')[0];
  const [selectedClass, setSelectedClass] = useState(availableClasses[0]?.id || '');
  const [date, setDate] = useState(today);
  const [localAttendance, setLocalAttendance] = useState({});
  const [saved, setSaved] = useState(false);

  const classStudents = students.filter(s => s.classId === selectedClass && s.status === 'active');

  React.useEffect(() => {
    const existing = attendance.filter(a => a.date === date && a.classId === selectedClass && a.entityType === 'student');
    const map = {};
    existing.forEach(a => { map[a.entityId] = a.status; });
    classStudents.forEach(s => { if (!map[s.id]) map[s.id] = 'present'; });
    setLocalAttendance(map);
    setSaved(false);
  }, [selectedClass, date]);

  const setStatus = (studentId, status) => setLocalAttendance(prev => ({ ...prev, [studentId]: status }));

  const saveAttendance = () => {
    const filtered = attendance.filter(a => !(a.date === date && a.classId === selectedClass && a.entityType === 'student'));
    const newRecords = classStudents.map(s => ({
      id: generateId('ATT'), date, entityType: 'student', entityId: s.id,
      classId: selectedClass, status: localAttendance[s.id] || 'present', markedBy: currentUser?.profileId || 'STF-001'
    }));
    setAttendance([...filtered, ...newRecords]);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const statusConfig = {
    present: { label: 'Present', color: 'bg-green-100 text-green-700 border-green-300', icon: Check },
    absent: { label: 'Absent', color: 'bg-red-100 text-red-700 border-red-300', icon: X },
    late: { label: 'Late', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Clock },
  };

  const counts = { present: 0, absent: 0, late: 0 };
  Object.values(localAttendance).forEach(s => { if (counts[s] !== undefined) counts[s]++; });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Student Attendance</h1><p className="text-gray-500 text-sm">Mark daily attendance</p></div>
        <button onClick={saveAttendance} className={`flex items-center gap-2 px-4 py-1 rounded-lg text-sm font-medium transition-colors ${saved ? 'bg-green-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>
          <Save size={16} />{saved ? 'Saved!' : 'Save Attendance'}
        </button>
      </div>
      <div className="flex gap-4 mb-6 flex-wrap">
        <div>
          <label className="block font-medium mb-1">Class</label>
          <select value={selectedClass} onChange={e=>setSelectedClass(e.target.value)} className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
            {availableClasses.map(c=><option key={c.id} value={c.id}>{c.name} - Sec {c.section}</option>)}
          </select>
        </div>
        <div>
          <label className="block font-medium mb-1">Date</label>
          <DateInput value={date} onChange={e=>setDate(e.target.value)} className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
        </div>
      </div>
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {Object.entries(counts).map(([status,count])=>(
          <div key={status} className={`rounded-lg px-4 py-1 text-center border ${statusConfig[status].color}`}>
            <div className="text-2xl font-bold">{count}</div>
            <div className="font-medium capitalize">{status}</div>
          </div>
        ))}
      </div>
      {/* Attendance List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-1 bg-gray-50 border-b border-gray-100">
          <p className="text-sm font-semibold">{classes.find(c=>c.id===selectedClass)?.name} - {classStudents.length} Students</p>
        </div>
        <div className="divide-y divide-gray-50">
          {classStudents.map((student, i) => {
            const status = localAttendance[student.id] || 'present';
            return (
              <div key={student.id} className="flex items-center justify-between px-4 py-1 hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <span className="text-sm w-6">{i+1}</span>
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">
                    {student.firstName?.[0]}{student.lastName?.[0]}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{student.firstName} {student.lastName}</p>
                    <p className="">Roll: {student.rollNumber}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {Object.entries(statusConfig).map(([s,cfg])=>(
                    <button key={s} onClick={()=>setStatus(student.id,s)}
                      className={`px-3 py-1 rounded-lg font-medium border transition-all ${status===s ? cfg.color : 'border-gray-200 hover:bg-gray-100'}`}>
                      {cfg.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {classStudents.length===0 && <div className="py-8 text-center">No students in this class</div>}
        </div>
      </div>
    </div>
  );
}
export default StudentAttendance;
